//React
import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import IconButton from "../inputs/iconButton";
//Assets
import xGreenIcon from "../../assets/svgs/x-green.svg";
//CSS
import "./modal.scss";

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal shell (ASB-3953): overlay, header with title and close, scrollable body,
 * footer slot. Escape and an overlay click close it; focus moves into the
 * dialog on open, Tab cycles inside it, and focus returns to the opener on
 * close. Same visual as the V2 builder's Destinations modal, which it is
 * extracted from. Content is the caller's; this only owns the frame.
 */
function Modal({
	open,
	title,
	onClose,
	children,
	footer,
	width,
	closeOnOverlay,
	closeOnEscape,
	showClose,
	className,
	ariaLabel,
	initialFocusRef,
}) {
	const dialogRef = useRef(null);
	const openerRef = useRef(null);
	const isOpen = open ?? true;

	const close = useCallback(() => {
		if (onClose) onClose();
	}, [onClose]);

	useEffect(() => {
		if (!isOpen) return undefined;
		openerRef.current = document.activeElement;
		const dialog = dialogRef.current;
		const target =
			(initialFocusRef && initialFocusRef.current) ||
			(dialog && dialog.querySelector(FOCUSABLE)) ||
			dialog;
		if (target && target.focus) target.focus();

		const onKey = (e) => {
			if (e.key === "Escape" && (closeOnEscape ?? true)) {
				e.stopPropagation();
				close();
				return;
			}
			if (e.key === "Tab" && dialog) {
				const items = Array.from(dialog.querySelectorAll(FOCUSABLE));
				if (items.length === 0) {
					e.preventDefault();
					return;
				}
				const first = items[0];
				const last = items[items.length - 1];
				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};
		document.addEventListener("keydown", onKey);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = prevOverflow;
			const opener = openerRef.current;
			if (opener && opener.focus && document.contains(opener)) opener.focus();
		};
	}, [isOpen, close, closeOnEscape, initialFocusRef]);

	if (!isOpen) return null;

	const node = (
		<div
			className="as-modal-overlay"
			data-testid="modal-overlay"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget && (closeOnOverlay ?? true)) close();
			}}
		>
			<div
				ref={dialogRef}
				className={["as-modal", className ?? ""].join(" ").trim()}
				role="dialog"
				aria-modal="true"
				aria-label={title ? undefined : ariaLabel}
				aria-labelledby={title ? "as-modal-title" : undefined}
				style={width ? { width: typeof width === "number" ? `${width}px` : width } : undefined}
				tabIndex={-1}
			>
				{title || (showClose ?? true) ? (
					<div className="as-modal__header">
						{title ? <h2 id="as-modal-title">{title}</h2> : <span />}
						{showClose ?? true ? (
							<IconButton
								icon={xGreenIcon}
								label="Close"
								className="as-modal__close"
								onClick={close}
							/>
						) : (
							""
						)}
					</div>
				) : (
					""
				)}
				<div className="as-modal__body">{children}</div>
				{footer ? <div className="as-modal__footer">{footer}</div> : ""}
			</div>
		</div>
	);

	return typeof document !== "undefined" && document.body
		? createPortal(node, document.body)
		: node;
}

Modal.propTypes = {
	/** default true; pass false to unmount */
	open: PropTypes.bool,
	title: PropTypes.string,
	onClose: PropTypes.func,
	children: PropTypes.node,
	/** right-aligned footer contents, e.g. a cancel link + Button */
	footer: PropTypes.node,
	/** px number or CSS length; default 600px */
	width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	closeOnOverlay: PropTypes.bool,
	closeOnEscape: PropTypes.bool,
	showClose: PropTypes.bool,
	className: PropTypes.string,
	/** required when there is no title */
	ariaLabel: PropTypes.string,
	/** ref to the element that should receive focus on open */
	initialFocusRef: PropTypes.shape({ current: PropTypes.any }),
};

export default Modal;
